import React, { useState, useMemo, useEffect } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { FEEDBACK_LABELS, ZONE_ROW, ZONE_BADGE } from './utils/feedbackStyles'
import { stripHtml } from './utils/stripHtml'

/* Minimal CSS for pseudo-class states and line-height adjustments */
const DROPDOWN_STYLES = `
.ngn-dropdown-prompt .dropdown-options:hover,
.ngn-dropdown-prompt .dropdown-options:focus {
  outline: 2px solid #007bff;
  outline-offset: 0.5px;
}
.ngn-dropdown-prompt {
  line-height: 1.75rem;
}
.ngn-dropdown-prompt.has-feedback {
  line-height: normal;
}
`

const BLOCK_TAG_RE = /<\/?(?:p|div|h[1-6]|ul|ol|li|blockquote|pre|table|tr|td|th|thead|tbody|tfoot|section|article|aside|header|footer|nav|figure|figcaption|main|details|summary|dl|dt|dd)[^>]*>/gi

const SELECT_STYLE = {
  margin: '0.5rem', border: '2px solid #9ca3af', backgroundColor: '#fff',
  cursor: 'pointer', borderRadius: '6px', transition: 'all 0.2s ease',
  display: 'inline', userSelect: 'none', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}

const ZONE_BASE = {
  display: 'inline-flex', flexDirection: 'column',
  padding: '0.2rem', margin: '2px', borderRadius: '8px', verticalAlign: 'middle',
}
const ZONE_CORRECT = { ...ZONE_BASE, border: '2px solid #28a745' }
const ZONE_WRONG = { ...ZONE_BASE, border: '2px solid #d4a017' }

/**
 * Splits a prompt string containing {0}, {1}, ... placeholders into segments.
 */
function parsePrompt(prompt) {
  const parts = []
  const regex = /\{(\d+)\}/g
  let lastIndex = 0
  let match
  while ((match = regex.exec(prompt)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'html', value: prompt.slice(lastIndex, match.index).replace(BLOCK_TAG_RE, ' ').trim() })
    }
    parts.push({ type: 'field', value: match[0], fieldIndex: parseInt(match[1], 10) })
    lastIndex = regex.lastIndex
  }
  if (lastIndex < prompt.length) {
    parts.push({ type: 'html', value: prompt.slice(lastIndex).replace(BLOCK_TAG_RE, ' ').trim() })
  }
  return parts
}

export const NGNDropdownQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  const initialSelections = useMemo(() => {
    if (!question.answer) return {}
    try {
      return JSON.parse(question.answer)
    } catch {
      return {}
    }
  }, [question.answer])

  const [selections, setSelections] = useState(initialSelections)

  useEffect(() => {
    const selected = Object.keys(JSON.parse(question.answer || '{}')).length
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const promptParts = useMemo(
    () => parsePrompt(question.data.prompt || ''),
    [question.data.prompt]
  )

  const handleChange = async (fieldGuid, optGuid) => {
    if (readonly || isSaving) return

    const updated = { ...selections, [fieldGuid]: optGuid }
    setSelections(updated)

    const answerJson = JSON.stringify(updated)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })

    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const { correctAnswer } = question
  const showFieldFeedback = showFeedback && showAnswer && correctAnswer

  // Build option text lookup per field
  const optTextByField = useMemo(() => {
    const map = {}
    for (const field of (question.data.fields || [])) {
      const fieldMap = {}
      for (const opt of field.options) {
        fieldMap[opt.guid] = opt.text
      }
      map[field.guid] = fieldMap
    }
    return map
  }, [question.data.fields])

  return (
    <>
      <FeedbackDisplay question={question} />
      <style>{DROPDOWN_STYLES}</style>
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
        <div className="d-flex flex-column align-items-start" style={{ width: '100%' }}>
          <div
            role="heading"
            aria-level={3}
            className="mb-2 text-left"
            dangerouslySetInnerHTML={{ __html: question.text }}
          />
          <div className={`ngn-dropdown-prompt d-block text-left${showFieldFeedback ? ' has-feedback' : ''}`} style={{ width: '100%', gap: '0.1rem' }}>
            {promptParts.map((part, i) => {
              if (part.type === 'html') {
                return part.value ? <span key={i} dangerouslySetInnerHTML={{ __html: part.value }} /> : null
              }
              const field = question.data.fields[part.fieldIndex]
              if (!field) return null

              if (showFieldFeedback) {
                const correctOptGuid = correctAnswer[field.guid]
                const selectedGuid = selections[field.guid]
                const optTexts = optTextByField[field.guid] || {}
                const isCorrectChoice = selectedGuid && selectedGuid === correctOptGuid

                return (
                  <span key={i} style={isCorrectChoice ? ZONE_CORRECT : ZONE_WRONG}>
                    {isCorrectChoice ? (
                      <span style={ZONE_ROW.correct}>
                        <span dangerouslySetInnerHTML={{ __html: optTexts[selectedGuid] || '' }} />
                        <span style={ZONE_BADGE.correct}>{FEEDBACK_LABELS.correct}</span>
                      </span>
                    ) : (
                      <>
                        {selectedGuid && (
                          <span style={ZONE_ROW.incorrect}>
                            <span dangerouslySetInnerHTML={{ __html: optTexts[selectedGuid] || '' }} />
                            <span style={ZONE_BADGE.incorrect}>{FEEDBACK_LABELS.incorrect}</span>
                          </span>
                        )}
                        {correctOptGuid && (
                          <span style={ZONE_ROW.missed}>
                            <span dangerouslySetInnerHTML={{ __html: optTexts[correctOptGuid] || '' }} />
                            <span style={ZONE_BADGE.missed}>{FEEDBACK_LABELS.missed}</span>
                          </span>
                        )}
                      </>
                    )}
                  </span>
                )
              }

              return (
                <select
                  key={i}
                  className="dropdown-options"
                  style={SELECT_STYLE}
                  name={field.guid}
                  value={selections[field.guid] || ''}
                  disabled={readonly || isSaving}
                  onChange={e => handleChange(field.guid, e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {field.options.map(opt => (
                    <option key={opt.guid} value={opt.guid}>
                      {stripHtml(opt.text)}
                    </option>
                  ))}
                </select>
              )
            })}
          </div>
        </div>
      </TabSplitLayout>
    </>
  )
}
